/**
 * Closing a period: the tally of card charges against card receipts, the work that is left, and
 * the export for the accountant.
 *
 * The headline counts what is still open: each charge without a receipt and each card receipt no
 * statement shows. A signed sum let one of each cancel out to "0" while nothing was complete, and
 * a bare "−6" said nothing (QA-0058). Zero with a statement imported means the period is complete.
 * Unambiguous charge/receipt pairs are linked on the way in (`useAutoLink`), so the number left
 * is the work a person actually has to do.
 */
import { Download, FileArchive, Link2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { toast } from "sonner";
import { BelegNumber } from "@/components/documents/document-badges";
import { EmptyState } from "@/components/shared/empty-state";
import { MatchDialog } from "@/components/banking/match-dialog";
import { PeriodPicker } from "@/components/shared/period-picker";
import { periodLabel } from "@/lib/utils/period-label";
import { Stat } from "@/components/shared/stat";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAssets } from "@/hooks/use-assets";
import {
  useAccounts,
  useAutoBook,
  useAutoLink,
  useBookingRules,
  useTransactions,
} from "@/hooks/use-banking";
import { useTrips } from "@/hooks/use-trips";
import { useAllDocuments, useCategories } from "@/hooks/use-documents";
import { track } from "@/lib/platform/analytics";
import { afaInput } from "@/lib/api/assets";
import type { Transaction } from "@/lib/api/banking";
import { tripInput } from "@/lib/api/trips";
import { openFilingPeriod, parsePeriodKey, type Period } from "@/lib/at/periods";
import { fileSafe, periodDocuments } from "@/lib/export/period-package";
import type { BookingTool } from "@/lib/api/accounting-export";
import { bmdBookings } from "@/lib/export/bmd";
import { datevBatchProblems, datevBookings } from "@/lib/export/datev";
import { moneyBookings } from "@/lib/export/money-bookings";
import { rzlBookings } from "@/lib/export/rzl";
import { planPartnerAccounts } from "@/lib/export/partners";
import { parseExportSettings } from "@/lib/export/settings";
import {
  buildCsv,
  DOCUMENT_COLUMNS,
  documentRows,
  exportLabels,
  downloadFile,
  MISSING_COLUMNS,
  missingReceiptRows,
} from "@/lib/export/tables";
import { formatDate, formatMoney } from "@/lib/utils/format";
import { periodCheck, transactionDate } from "@/lib/banking/period-close";
import { cn } from "@/lib/utils/cn";
import { useWorkspace } from "@/lib/session/workspace-context";

/** The booking writers, to name what would stay out of the file before anyone exports. */
const PREVIEW = { bmd: bmdBookings, rzl: rzlBookings, datev: datevBookings } as const;

export function ClosePage() {
  const { t } = useTranslation();
  const { workspace, canWrite, plan, limits } = useWorkspace();
  // BMD, RZL and DATEV files from Standard up (Start and a read-only business get the ZIP).
  const toolsAllowed = !plan || limits?.accountingExport === true;
  const basis = workspace?.accountingBasis ?? "cash";
  // The period lives in React and is mirrored into ?p=, so a reload or a trip to a document and
  // back keeps it. Without one: on the 3rd of October you are closing September.
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriodState] = useState(
    () =>
      parsePeriodKey(searchParams.get("p")) ??
      openFilingPeriod(new Date().toISOString().slice(0, 10), "monthly"),
  );
  function setPeriod(next: Period) {
    setPeriodState(next);
    setSearchParams({ p: next.key }, { replace: true });
  }
  const [matching, setMatching] = useState<Transaction | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const { data: accounts, isPending: accountsPending } = useAccounts(workspace?.id);
  const { data: transactions, isPending: transactionsPending } = useTransactions(workspace?.id);
  const { data: documents, isPending: documentsPending } = useAllDocuments(workspace?.id);
  const { data: categories } = useCategories(workspace?.id);
  const { data: assets } = useAssets(workspace?.id);
  const { data: trips } = useTrips(workspace?.id);

  useAutoLink(transactions, documents, canWrite, (count) =>
    toast.success(t("close.autoLinked", { count })),
  );
  const { data: rules } = useBookingRules(workspace?.id);
  useAutoBook(transactions, rules, canWrite, (count) =>
    toast.success(t("booking.autoBooked", { count })),
  );

  const check = useMemo(
    () => periodCheck(accounts ?? [], transactions ?? [], documents ?? [], period),
    [accounts, transactions, documents, period],
  );
  const exportable = useMemo(
    () => periodDocuments(documents ?? [], period, basis),
    [documents, period, basis],
  );
  const candidates = useMemo(() => {
    const claimed = new Set((transactions ?? []).map((row) => row.document).filter(Boolean));
    return (documents ?? []).filter(
      (document) => !claimed.has(document.id) && document.status !== "archived",
    );
  }, [transactions, documents]);

  const missing = check.accounts.flatMap((entry) =>
    entry.missing.map((transaction) => ({ transaction, account: entry.account })),
  );
  const open = check.missingCount + check.unmatchedReceipts.length;
  const perKind = { er: 0, ar: 0, ka: 0 };
  for (const document of exportable)
    perKind[document.kind.toLowerCase() as "er" | "ar" | "ka"] += 1;
  const notBooked = exportable.filter((document) => document.status !== "booked").length;

  const settings = useMemo(
    () => parseExportSettings(workspace?.accountingExport),
    [workspace?.accountingExport],
  );
  const [chosenFormat, setFormat] = useState<"documents" | BookingTool>(
    settings.tool || "documents",
  );
  const format = toolsAllowed ? chosenFormat : "documents";
  // What would keep a booked document out of the BMD file, shown before anyone clicks.
  const bookingProblems = useMemo(() => {
    if (format === "documents") return [];
    const booked = exportable.filter((document) => document.status === "booked");
    return PREVIEW[format](booked, {
      categories: categories ?? [],
      partnerNumbers: planPartnerAccounts(booked, [], settings).numbers,
      settings,
      basis,
      paidFrom: () => undefined,
      documentPath: () => "",
    }).problems;
  }, [format, exportable, categories, settings, basis]);

  // Bank rows without a receipt, AfA and trips: in the booking file as Sachkonto bookings.
  const ledgerAssets = useMemo(
    () =>
      (assets ?? []).map((asset) => ({
        ...afaInput(asset),
        workspace: asset.workspace,
        name: asset.name,
        account: asset.account,
      })),
    [assets],
  );
  const ledgerTrips = useMemo(
    () =>
      (trips ?? []).map((trip) => ({
        ...tripInput(trip),
        workspace: trip.workspace,
        destination: trip.destination,
        purpose: trip.purpose,
      })),
    [trips],
  );
  const money = useMemo(
    () =>
      format === "documents"
        ? null
        : moneyBookings({
            period,
            transactions: transactions ?? [],
            accounts: accounts ?? [],
            categories: categories ?? [],
            assets: ledgerAssets,
            trips: ledgerTrips,
            cashAccount: settings.cashAccount,
          }),
    [format, period, transactions, accounts, categories, ledgerAssets, ledgerTrips, settings],
  );

  const fiscalYearStartMonth = workspace?.fiscalYearStartMonth || 1;
  const batchProblems = useMemo(
    () => (format === "datev" ? datevBatchProblems(settings, period, fiscalYearStartMonth) : []),
    [format, settings, period, fiscalYearStartMonth],
  );

  async function exportPeriod() {
    if (!workspace) return;
    setProgress({ done: 0, total: exportable.length });
    try {
      const extras = [
        {
          name: t("periodExport.files.journal", { period: period.key }),
          content: buildCsv(
            DOCUMENT_COLUMNS.map((column) => t(`export.documents.${column}`)),
            documentRows(exportable, categories ?? [], exportLabels(t)),
          ),
        },
      ];
      if (check.missingCount > 0) {
        extras.push({
          name: t("periodExport.files.missing", { period: period.key }),
          content: buildCsv(
            MISSING_COLUMNS.map((column) => t(`export.missing.${column}`)),
            missingReceiptRows(check.accounts),
          ),
        });
      }
      const context = {
        categories: categories ?? [],
        transactions: transactions ?? [],
        accounts: accounts ?? [],
        workspaceName: workspace.name,
        today: new Date().toISOString().slice(0, 10),
      };
      const onProgress = (done: number, total: number) => setProgress({ done, total });
      const name = fileSafe(workspace.name) || "export";
      if (format !== "documents") {
        const { buildAccountingZip } = await import("@/lib/api/accounting-export");
        const result = await buildAccountingZip({
          tool: format,
          workspaceId: workspace.id,
          documents: exportable,
          categories: categories ?? [],
          transactions: transactions ?? [],
          accounts: accounts ?? [],
          assets: ledgerAssets,
          trips: ledgerTrips,
          settings,
          basis,
          period,
          fiscalYearStartMonth,
          context,
          extras,
          t,
          onProgress,
        });
        const label = t(`accountingExport.tools.${format}`);
        downloadFile(
          `${name}-${period.key}-${format.toUpperCase()}.zip`,
          new Blob([result.zip as BlobPart], { type: "application/zip" }),
        );
        toast.success(t("accountingExport.exportedTool", { tool: label, booked: result.booked }));
        track("accounting_exported", {
          tool: format,
          booked: result.booked,
          problems: result.problems.length,
        });
        return;
      }
      const { buildPeriodZip } = await import("@/lib/api/period-export");
      const zip = await buildPeriodZip({ documents: exportable, context, extras, t, onProgress });
      downloadFile(
        `${name}-${period.key}.zip`,
        new Blob([zip as BlobPart], { type: "application/zip" }),
      );
      toast.success(t("close.exported"));
      track("period_exported", { documents: exportable.length, complete: check.complete });
    } catch (error) {
      toast.error(
        t("close.exportFailed", {
          message: error instanceof Error ? error.message : String(error),
        }),
      );
    } finally {
      setProgress(null);
    }
  }

  const loading = accountsPending || transactionsPending || documentsPending;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("close.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("close.subtitle")}</p>
      </div>

      <PeriodPicker value={period} onChange={setPeriod} />

      {loading && <Skeleton className="h-40 w-full" aria-busy="true" />}

      {!loading && check.noStatement && (
        <EmptyState
          title={t("close.noStatement")}
          description={t("close.noStatementText")}
          testId="no-statement"
          action={
            <Button asChild variant="outline">
              <Link to="/banking">{t("close.openBanking")}</Link>
            </Button>
          }
        />
      )}

      {!loading && !check.noStatement && (
        <section
          className="grid gap-6 rounded-lg border bg-card p-6 md:grid-cols-[minmax(0,16rem)_1fr]"
          data-testid="period-tally"
        >
          <Stat
            emphasis
            label={t("close.tally")}
            value={<span data-testid="tally">{open}</span>}
            tone={check.complete ? "success" : "warning"}
            hint={
              check.complete
                ? t("close.complete")
                : t("close.tallyHint", {
                    missing: check.missingCount,
                    extra: check.unmatchedReceipts.length,
                  })
            }
          />
          <dl className="divide-y text-sm">
            {check.accounts.map((entry) => (
              <div
                key={entry.account.id}
                className="flex items-baseline justify-between gap-4 py-2"
                data-testid="tally-account"
              >
                <dt>
                  <span className="font-medium">{entry.account.label}</span>{" "}
                  <span className="text-muted-foreground">
                    {t("close.charges", { count: entry.charges })}
                  </span>
                </dt>
                <dd
                  className={cn(
                    "num",
                    entry.missing.length > 0 ? "font-medium text-warning" : "text-success",
                  )}
                >
                  {entry.missing.length > 0
                    ? t("close.missingOf", {
                        count: entry.missing.length,
                        total: formatMoney(entry.missingTotal),
                      })
                    : t("close.balanced")}
                </dd>
              </div>
            ))}
            {check.unmatchedReceipts.length > 0 && (
              <div className="flex items-baseline justify-between gap-4 py-2">
                <dt className="font-medium">
                  {t("close.extra", { count: check.unmatchedReceipts.length })}
                </dt>
                <dd className="font-medium text-warning num">{check.unmatchedReceipts.length}</dd>
              </div>
            )}
            {check.complete && (
              <p className="py-2 text-muted-foreground" data-testid="period-complete">
                {t("close.completeText")}
              </p>
            )}
          </dl>
        </section>
      )}

      {check.reviewCount > 0 && (
        <p className="text-sm text-warning" data-testid="period-review">
          {t("close.review", { count: check.reviewCount })}{" "}
          <Link
            to="/banking"
            className="text-primary underline decoration-primary/30 underline-offset-4 hover:decoration-primary"
          >
            {t("close.openBanking")}
          </Link>
        </p>
      )}

      {missing.length > 0 && (
        <section className="space-y-3" data-testid="period-missing">
          <div>
            <h2 className="font-medium">{t("close.missing", { count: missing.length })}</h2>
            <p className="text-sm text-muted-foreground">{t("close.missingText")}</p>
          </div>
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("banking.date")}</TableHead>
                  <TableHead>{t("banking.counterparty")}</TableHead>
                  <TableHead className="text-right">{t("banking.amount")}</TableHead>
                  <TableHead className="w-32" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {missing.map(({ transaction, account }) => (
                  <TableRow key={transaction.id} data-testid="missing-row">
                    <TableCell className="text-muted-foreground num">
                      {formatDate(transactionDate(transaction))}
                    </TableCell>
                    <TableCell className="max-w-72">
                      <p className="truncate font-medium">{transaction.counterparty || "—"}</p>
                      <p className="truncate text-xs text-muted-foreground">{account.label}</p>
                    </TableCell>
                    <TableCell className="text-right font-medium num">
                      {formatMoney(transaction.amount, transaction.currency)}
                    </TableCell>
                    <TableCell className="text-right">
                      {canWrite && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setMatching(transaction)}
                          data-testid="match-transaction"
                        >
                          <Link2 /> {t("banking.attach")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      )}

      {check.unmatchedReceipts.length > 0 && (
        <section className="space-y-3" data-testid="period-extra">
          <div>
            <h2 className="font-medium">
              {t("close.extra", { count: check.unmatchedReceipts.length })}
            </h2>
            <p className="text-sm text-muted-foreground">{t("close.extraText")}</p>
          </div>
          <ul className="divide-y rounded-lg border bg-card">
            {check.unmatchedReceipts.map((document) => (
              <li key={document.id} data-testid="extra-row">
                <Link
                  to={`/documents/${document.id}`}
                  className="flex items-center gap-4 px-4 py-2.5 text-sm hover:bg-accent/40"
                >
                  <BelegNumber value={document.docNumber} />
                  <span className="min-w-0 flex-1 truncate font-medium">
                    {document.partnerName || document.originalName}
                  </span>
                  <span className="text-muted-foreground num">
                    {formatDate(document.paidDate || document.issueDate)}
                  </span>
                  <span className="w-24 text-right font-medium num">
                    {formatMoney(
                      (document.grossTotal ?? 0) + (document.tip ?? 0),
                      document.currency,
                    )}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {check.afterLastStatement > 0 && (
        <p className="text-sm text-muted-foreground" data-testid="after-statement">
          {t("close.afterStatement", { count: check.afterLastStatement })}
        </p>
      )}

      {!loading && (
        <section className="space-y-4 rounded-lg border p-6" data-testid="period-export">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-xl space-y-1">
              <h2 className="font-medium">{t("close.exportTitle")}</h2>
              <p className="text-sm text-muted-foreground">
                {format === "documents"
                  ? t("close.exportText")
                  : t(`accountingExport.${format}Text`)}
              </p>
              {/* Unconfirmed until a Kanzlei has imported one (AGENTS.md §13), said where it is chosen. */}
              {format !== "documents" && (
                <p className="text-sm" data-testid="export-beta">
                  <span className="mr-1.5 rounded-sm bg-warning/15 px-1.5 py-px text-xs font-medium text-warning">
                    {t("accountingExport.betaLabel")}
                  </span>
                  {t("accountingExport.betaShort")}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={format} onValueChange={(value) => setFormat(value as typeof format)}>
                <SelectTrigger
                  className="w-48"
                  aria-label={t("accountingExport.format")}
                  data-testid="export-format"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="documents">
                    {t("accountingExport.formats.documents")}
                  </SelectItem>
                  {(["bmd", "rzl", "datev"] as const).map((tool) => (
                    <SelectItem key={tool} value={tool} disabled={!toolsAllowed}>
                      {t(`accountingExport.formats.${tool}`)}
                      {!toolsAllowed && ` · ${t("plans.names.standard")}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={exportPeriod}
                disabled={exportable.length === 0 || progress !== null || batchProblems.length > 0}
                data-testid="export-period"
              >
                {progress ? <FileArchive /> : <Download />}
                {progress
                  ? t("close.exporting", progress)
                  : format === "bmd"
                    ? t("accountingExport.exportBmd")
                    : format === "rzl"
                      ? t("accountingExport.exportRzl")
                      : format === "datev"
                        ? t("accountingExport.exportDatev")
                        : t("close.export")}
              </Button>
            </div>
          </div>
          {exportable.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("close.nothingToExport")}</p>
          ) : (
            <div className="space-y-1 text-sm">
              <p className="font-medium" data-testid="export-counts">
                {periodLabel(period, t)} ·{" "}
                <span className="num">{t("close.perKind", perKind)}</span>
              </p>
              {notBooked > 0 && (
                <p className="text-muted-foreground">
                  {t("close.notBooked", { count: notBooked })}
                </p>
              )}
              {!check.complete && !check.noStatement && (
                <p className="text-warning">{t("close.incomplete")}</p>
              )}
              {batchProblems.map((problem) => (
                <p key={problem} className="text-warning" data-testid="batch-problem">
                  {t(`accountingExport.batchProblems.${problem}`)}
                </p>
              ))}
              {money && money.bookings.length > 0 && (
                <p className="text-muted-foreground" data-testid="plain-count">
                  {t("accountingExport.plainCount", { count: money.bookings.length })}
                </p>
              )}
              {money && money.problems.length > 0 && (
                <div className="pt-2" data-testid="money-problems">
                  <p className="font-medium text-warning">
                    {t("accountingExport.moneyProblemsTitle", { count: money.problems.length })}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {money.problems.map((problem) => (
                      <li key={problem.transactionId} className="flex flex-wrap gap-x-2">
                        <Link
                          to="/banking"
                          className="whitespace-nowrap num underline-offset-4 hover:underline"
                        >
                          {formatDate(problem.date)} {problem.text}
                        </Link>
                        <span className="text-muted-foreground">
                          {t(`accountingExport.moneyProblems.${problem.reason}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {bookingProblems.length > 0 && (
                <div className="pt-2" data-testid="booking-problems">
                  <p className="font-medium text-warning">
                    {t("accountingExport.problemsTitle", { count: bookingProblems.length })}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {bookingProblems.map((problem) => (
                      <li key={problem.documentId} className="flex gap-2">
                        <Link
                          to={`/documents/${problem.documentId}`}
                          className="ledger-id underline-offset-4 hover:underline"
                        >
                          {problem.docNumber}
                        </Link>
                        <span className="text-muted-foreground">
                          {t(`accountingExport.problems.${problem.reason}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      <MatchDialog
        transaction={matching}
        documents={candidates}
        onOpenChange={(open) => !open && setMatching(null)}
      />
    </div>
  );
}
